import {resolveBackground} from '#core/utils/color-utils.js';
import {absoluteUrlToInternalResource} from '#core/utils/url-utils.js';
import {merge} from '#core/utils/config-utils.js';
import {icon} from '#core/utils/style-utils.js';

import {CLOSE_BUTTON_TYPES, generateCloseButton} from '#core/application/modules/tiles/close-button/index.js';
import {generateBetBaseInfo} from '#core/application/modules/tiles/bet-base-info/index.js';
import {generateBetLegsInfo} from '#core/application/modules/tiles/bet-legs-info/index.js';
import {generateSecondaryButton} from '#core/application/modules/tiles/button/index.js';
import {generateSimplePopover} from '#core/application/modules/tiles/popover/index.js';

export function betSharingDialogFactory(PALETTE) {
    const BET_SHARING_BET_BASE_INFO = generateBetBaseInfo(PALETTE.BET_SHARING_BET_BASE_INFO);
    const SHARE_BET_CONTENT = {
        container: {
            style: {
                ...resolveBackground(PALETTE.BET_SHARING_CONTENT_CONTAINER_BG),
                backgroundImage: `url(${absoluteUrlToInternalResource('images/bet-sharing/content-bg.png')})`,
            },
        },
        logo: {
            src: 'bet-sharing/logo.svg',
            fallbackSrc: 'generic/logo.svg',
            style: {
                maxHeight: 30,
                maxWidth: 100,
            },
        },
        betTypeName: {
            style: {
                ...PALETTE.BET_SHARING_BET_TYPE_TEXT,
            },
        },
        totalOdds: BET_SHARING_BET_BASE_INFO.betTotalOdds,
        betContentContainer: {
            style: {
                ...resolveBackground(PALETTE.BET_SHARING_BET_LEGS_CONTAINER_BG),
                borderRadius: PALETTE.BET_SHARING_BET_LEGS_CONTAINER_RADIUS,
                boxShadow: PALETTE.BET_SHARING_BET_LEGS_CONTAINER_SHADOW,
            },
        },
        betLegsDetails: merge(generateBetLegsInfo(PALETTE.BET_SHARING_BET_LEGS_INFO), {
            container: {
                style: {
                    padding: 0,
                },
            },
            matchAccaSelectionItem: {
                voidReasonMessage: {
                    notification: {
                        icon: {
                            src: 'semantic-info',
                        },
                    },
                },
                selectionStatusIcon: {
                    svgPath: null, // reset svgPath to render the icon as an SVG sprite instead of an image
                },
            },
            legStatusIcon: {
                svgPath: null, // reset svgPath to render the icon as an SVG sprite instead of an image
            },
            voidReasonMessage: {
                notification: {
                    icon: {
                        src: 'semantic-info',
                    },
                },
            },
        }),
        betSummary: {
            betStatusIcon: {
                svgPath: null, // reset svgPath to render the icon as an SVG sprite instead of an image
                base: {
                    style: {
                        width: 16,
                        height: 16,
                    },
                },
            },
            betFundsInfo: BET_SHARING_BET_BASE_INFO.betFundsInfo.normal,
            stake: {
                style: {
                    ...PALETTE.BET_SHARING_BET_SUMMARY_TEXT,
                },
            },
            cashoutContainer: {
                style: {
                    ...PALETTE.BET_SHARING_BET_SUMMARY_TEXT,
                },
            },
        },
    };

    return {
        ShareDialog: {
            dialog: {
                centered: {
                    maxHeight: 'min(800px, calc(100% - 100px))',
                },
                dialogContent: {
                    style: {
                        ...resolveBackground(PALETTE.BET_SHARING_DIALOG_CONTAINER_BG),
                        borderRadius: PALETTE.BET_SHARING_DIALOG_CONTAINER_BORDER_RADIUS,
                        boxShadow: PALETTE.BET_SHARING_DIALOG_CONTAINER_SHADOW,
                    },
                },
                closeButton: generateCloseButton(PALETTE.BET_SHARING_DIALOG_CLOSE_BUTTON, CLOSE_BUTTON_TYPES.CIRCULAR),
                header: {
                    title: {
                        style: {
                            ...PALETTE.BET_SHARING_DIALOG_TITLE_TEXT,
                        },
                    },
                },
            },
            footer: {
                animatedLinkButton: {
                    spinnerWithContentContainer: {
                        itemSpacing: 8,
                    },
                    button: merge(generateSecondaryButton(PALETTE.BET_SHARING_DIALOG_COPY_LINK_BUTTON), {
                        style: {
                            base: {
                                height: 'auto',
                                minHeight: '40px',
                                padding: '4px 8px',
                            },
                        },
                    }),
                    spinner: {
                        image: icon(PALETTE.BET_SHARING_DIALOG_COPY_LINK_BUTTON.SECONDARY_BUTTON_ICON_COLOR),
                    },
                },
                animatedLinkButtonHover: {
                    spinner: {
                        image: icon(PALETTE.BET_SHARING_DIALOG_COPY_LINK_BUTTON.SECONDARY_BUTTON_SELECTED_ICON_COLOR),
                    },
                },
                linkButtonIcon: icon(PALETTE.BET_SHARING_DIALOG_COPY_LINK_BUTTON.SECONDARY_BUTTON_ICON_COLOR),
                linkButtonIconDisabled: icon(
                    PALETTE.BET_SHARING_DIALOG_COPY_LINK_BUTTON.SECONDARY_BUTTON_DISABLED_ICON_COLOR
                ),
                linkButtonIconHover: icon(
                    PALETTE.BET_SHARING_DIALOG_COPY_LINK_BUTTON.SECONDARY_BUTTON_SELECTED_ICON_COLOR
                ),
                copyLinkTooltip: {
                    popup: merge(
                        generateSimplePopover(
                            PALETTE.BET_SHARING_DIALOG_COPY_LINK_POPOVER_BG,
                            PALETTE.BET_SHARING_DIALOG_COPY_LINK_POPOVER_BORDER_RADIUS,
                            PALETTE.BET_SHARING_DIALOG_COPY_LINK_POPOVER_SHADOW
                        ),
                        {
                            arrowheadOffset: 'calc(50% - 5px)',
                            container: {
                                style: {
                                    width: 'auto',
                                    minWidth: '104px',
                                    maxWidth: '150px',
                                },
                            },
                        }
                    ),
                    textLabel: {
                        container: {
                            padding: 0,
                        },
                        style: {
                            whiteSpace: 'initial',
                            textAlign: 'center',
                            ...PALETTE.BET_SHARING_DIALOG_COPY_LINK_POPOVER_POPOVER_TEXT,
                        },
                    },
                },
                // screenshot
                screenshotButton: merge(generateSecondaryButton(PALETTE.BET_SHARING_DIALOG_MAKE_SCREENSHOT_BUTTON), {
                    style: {
                        base: {
                            height: 'auto',
                            minHeight: '40px',
                            padding: '4px 8px',
                        },
                    },
                }),
                screenshotButtonIcon: icon(
                    PALETTE.BET_SHARING_DIALOG_MAKE_SCREENSHOT_BUTTON.SECONDARY_BUTTON_ICON_COLOR
                ),
                screenshotButtonIconHover: icon(
                    PALETTE.BET_SHARING_DIALOG_MAKE_SCREENSHOT_BUTTON.SECONDARY_BUTTON_SELECTED_ICON_COLOR
                ),
            },
        },
        ShareBetContent: SHARE_BET_CONTENT,
        ShareBetslip: {
            innerContainer: SHARE_BET_CONTENT.container,
            logo: SHARE_BET_CONTENT.logo,
            betContentContainer: SHARE_BET_CONTENT.betContentContainer,
        },
    };
}
